<template>
  <div>
    <div class="outer-filter-container">
      <div class="j-container filter-container">
        <transition-group name="vehicle-type">
          <div
            class="filter"
            :class="{ active: selectedType === v.vehicleType }"
            v-for="v in vehicleTypeList"
            :key="v.id"
            @click="handleTypeChange(v.vehicleType)"
          >
            {{ $t(v.vehicleType) }}
          </div>
        </transition-group>

        <div v-if="oVehicleTypeList.length > 7">
          <a style="padding: 12px 30px;" @click="toggleVehicleType"
            >{{ expand ? $t('product.collapse') : $t('product.expand') }}
            <UpOutlined v-if="expand" /> <DownOutlined v-if="!expand" />
          </a>
        </div>
      </div>
    </div>
    <Banner type="product" />
    <div class="page-container j-container-box-shadow container">
      <span class="j-title title">{{ getTitle() }}</span>

      <a-spin :spinning="loading">
        <div>
          <a-row :gutter="[45, 45]" v-if="vehicleList.length > 0">
            <a-col
              :xs="24"
              :sm="24"
              :md="8"
              v-for="item in vehicleList"
              :key="item.id"
            >
              <ProductItem :vehicle="item" />
            </a-col>
          </a-row>
          <div class="j-no-result" v-if="loaded && vehicleList.length === 0">
            <img
              alt="no-data"
              src="@/assets/images/noData.png"
              width="164"
              height="141"
            />
            <span class="no-data-msg j-content">
              {{ $t('common.coming_soon') }}
            </span>
          </div>
        </div>
      </a-spin>

      <div class="pagination-container" v-if="vehicleList.length > 0">
        <a-pagination
          v-model:current="pageNo"
          :page-size-options="pageSizeOptions"
          :total="total"
          show-size-changer
          v-model:pageSize="pageSize"
          @showSizeChange="onShowSizeChange"
          @change="onChange"
          show-quick-jumper
        >
          <template #buildOptionText="props">
            <span>{{ props.value }} {{ $t('common.page_per_item_text') }}</span>
          </template>
        </a-pagination>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { DownOutlined, UpOutlined } from '@ant-design/icons-vue';
import Banner from '@/components/banner/Banner.vue';
import ProductItem from './product/ProductItem.vue';
import { getVehicleTypeList, getVehicleList } from '@/api/apis';

interface ProductProps {
  vehicleTypeList: any[];
  vehicleList: any[];
  oVehicleTypeList: any[];
  [key: string]: any;
}

export default defineComponent({
  name: 'Product',
  components: {
    Banner,
    ProductItem,
    DownOutlined,
    UpOutlined
  },
  data(): ProductProps {
    const selectedType = '';
    return {
      loading: false,
      loaded: false,
      selectedType,
      pageNo: 1,
      pageSize: 6,
      pageSizeOptions: ['6', '12'],
      current: 1,
      total: 0,
      vehicleTypeList: [],
      vehicleList: [],
      oVehicleTypeList: [],
      expand: false
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    toggleVehicleType() {
      this.expand = !this.expand;
      if (this.expand) {
        this.vehicleTypeList = [...this.oVehicleTypeList];
      } else {
        this.vehicleTypeList = this.oVehicleTypeList.slice(0, 7);
      }
    },
    handleTypeChange(vehicleType) {
      if (vehicleType !== this.selectedType) {
        this.selectedType = vehicleType;
        this.loaded = false;

        this.$router.push(`/products?vehicleType=${this.selectedType}`);
      }
    },
    getTitle() {
      const item = this.oVehicleTypeList.find(
        (s: any) => s.vehicleType === this.selectedType
      );
      return item ? this.$t(item.vehicleType) : '';
    },
    async loadData() {
      const res = await getVehicleTypeList();

      if (res.success) {
        this.oVehicleTypeList = res.result || [];
        this.vehicleTypeList = this.oVehicleTypeList.slice(0, 7);

        // 路由
        if (!this.selectedType) {
          if (this.vehicleTypeList.length > 0) {
            this.selectedType = (this.vehicleTypeList[0] as any).vehicleType;
          }
        }

        if (this.selectedType) {
          // await this.loadVehicleList();
          this.$router.push(`/products?vehicleType=${this.selectedType}`);
        }
      }
    },
    async loadVehicleList() {
      this.loading = true;
      const vRes = await getVehicleList({
        vehicleType: this.selectedType,
        pageNo: this.pageNo,
        pageSize: this.pageSize
      });

      if (vRes.success) {
        this.vehicleList = vRes.result.records || [];
        this.total = vRes.result.total;
      }

      this.loading = false;

      this.loaded = true;
    },
    onShowSizeChange() {
      this.pageNo = 1;
      this.loadVehicleList();
    },
    onChange() {
      this.loadVehicleList();
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(val) {
        if (val.name === 'Products') {
          const vehicleType = val.query.vehicleType;
          this.selectedType = vehicleType;

          if (!this.selectedType) {
            if (this.vehicleTypeList.length > 0) {
              this.selectedType = (this.vehicleTypeList[0] as any).vehicleType;
              this.$router.push(`/products?vehicleType=${this.selectedType}`);
            }
          } else {
            this.loadVehicleList();
          }
        }
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.outer-filter-container {
  background: $secondary-color1;

  .filter-container {
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .filter {
      padding: 12px 30px;
      cursor: pointer;
      color: $primary-secondary-forecolor;

      &.active {
        background: $primary-color;
        color: white;
        transition: all 0.5s;
      }
    }
  }
}

.container {
  padding: 0 49px;
  background-color: white;

  .title {
    padding: 32px 0 29px 0;
    display: block;
  }

  .pagination-container {
    padding: 38px 0 40px 0;
    text-align: right;
  }
}

.vehicle-type-enter-active,
.vehicle-type-leave-active {
  transition: opacity 250ms ease;
}

.vehicle-type-enter-from,
.vehicle-type-leave-to {
  opacity: 0;
}
</style>
